import type { ComputedRef } from 'vue'
import type Vue from 'vue'
import type { EpbSelectOption } from '../../components/shared/epb-select'
import type { ContextState } from '../../models'
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { CONTAINER } from '../../config'
import { TYPES } from '../../config/inversify.types'
import { EndpointContextEnum } from '../../enums'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { useContextQuery } from '../use-context-query'
import { useEndpoints } from '../use-endpoints'
import { mapGetters } from '../utils'
import { useX } from '../x/use-x'

export function resolveExplainEndpointId(
  contextEndpointId?: string,
  configuredDefaultEndpointId?: string,
): string {
  return contextEndpointId || configuredDefaultEndpointId || 'search'
}

export interface FieldValueSelectValue {
  name: string | undefined
  value: string | undefined
  inheritance?: boolean
}

export interface UseExplainBrowseComposable {
  endpointId: ComputedRef<string>
  endpointContext: ComputedRef<EndpointContextEnum>
  isBrowseContext: ComputedRef<boolean>
  browseFieldOptions: ComputedRef<Array<EpbSelectOption>>
  browseFieldValue: ComputedRef<FieldValueSelectValue>
  context: ComputedRef<ContextState>
  onBrowseFieldValueChange: (value: FieldValueSelectValue) => Promise<void>
}

export function useExplainBrowse(): UseExplainBrowseComposable {
  const {
    proxy: { $store },
  } = getCurrentInstance() as { proxy: Vue }

  const { query, setQuery } = useContextQuery()
  const { endpoints, defaultEndpointId } = useEndpoints()
  const x = useX()

  const { context } = mapGetters($store, CONTEXT_CONSTS.moduleName, {
    context: CONTEXT_CONSTS.getters.context,
  })

  const endpointId = computed(() =>
    resolveExplainEndpointId(context.value.endpointId, defaultEndpointId.value),
  )

  const endpointContext = computed<EndpointContextEnum>(() => {
    return (endpoints.value.find(endpoint => endpoint.value === endpointId.value)?.context ??
      EndpointContextEnum.QUERY) as EndpointContextEnum
  })

  const isBrowseContext = computed(() => endpointContext.value === EndpointContextEnum.FILTER)

  const toolConfig = CONTAINER.get<any>(TYPES.QueryRankingToolConfigProvider)()

  const browseFieldOptions = computed<Array<EpbSelectOption>>(
    () =>
      toolConfig.browseFields?.map((browseField: any) => ({
        value: browseField.id,
        label: browseField.displayName,
        isCategory: browseField.isCategory,
      })) ?? [],
  )

  const browseFieldValueState = ref<FieldValueSelectValue>({ name: undefined, value: undefined })

  watch(
    () => [endpointId.value, endpoints.value],
    () => {
      const defaultEndpoint = endpoints.value.find(endpoint => endpoint.value === endpointId.value)
      if (endpointContext.value === EndpointContextEnum.FILTER) {
        browseFieldValueState.value = {
          name: defaultEndpoint?.defaultBrowseField,
          value: defaultEndpoint?.defaultBrowseValue,
        }
      }
    },
    { immediate: true },
  )

  const browseFieldValue = computed<FieldValueSelectValue>(() => {
    if (query.value && query.value.includes(':')) {
      const [name, value] = query.value.split(':')
      return { name, value }
    }
    return browseFieldValueState.value
  })

  const onBrowseFieldValueChange = async (value: FieldValueSelectValue): Promise<void> => {
    browseFieldValueState.value = value

    if (value.name && value.value) {
      const browseQuery = `${value.name}:${value.value}`
      await setQuery(browseQuery)
      x.setQueryFromUrl(browseQuery)
    } else if (!value.name) {
      await setQuery('')
      x.clearQuery()
    }
  }

  return {
    endpointId,
    endpointContext,
    isBrowseContext,
    browseFieldOptions,
    browseFieldValue,
    context,
    onBrowseFieldValueChange,
  }
}
